import { v4 as uuid } from 'uuid'
import Chart from '../chart'
import { ChartEvents } from '../chart.events'
import type DrawingBase from '../drawingTools/drawing.base'
import { getDrawingClass } from '../drawingTools/drawingClassMapAccess'
import {
    ChartDrawingItem,
    DrawingDisplayType,
    DrawingEvents,
    DrawingSettingGroup,
    DrawingSettings,
    DrawingTypes
} from '../drawingTools/drawing.interface'

export type DrawingManagerOptions = {
    chart: Chart
}

class DrawingManager {
    chart: Chart
    drawings: Record<string, DrawingBase<any, any, any>> = {}
    drawingsSequence: string [] = []
    magnetMode = false
    destroyed = false

    constructor(options: DrawingManagerOptions) {
        this.chart = options.chart
        this.magnetMode = Boolean(window.localStorage.getItem('chart_magnet_mode') || '')
        this.chart.subscribeEvent(ChartEvents.REMOVE_DRAWING, this.handleDrawingRemove)
    }

    addDrawing<T extends DrawingTypes>(type: T, displayType: DrawingDisplayType, options: Record<string, any>, settings: Partial<DrawingSettings<T>>, id?: string, initialState: Record<string, any> = {}, paneId?: string, seriesId?: string, referenceId?: string): DrawingBase<any, any, any> | null {
        if (this.destroyed || this.chart.destroyed) {
            return null
        }
        if (!this.chart.initialized || !this.chart.chartApi) {
            setTimeout(() => {
                if (this.destroyed || this.chart.destroyed) return
                this.addDrawing(type, displayType, options, settings, id, initialState, paneId, seriesId, referenceId)
            }, 100)
            return null
        }
        if (id && this.drawings[id]) {
            return this.drawings[id]
        }
        const DrawingClass = getDrawingClass(type)
        if(DrawingClass) {
            const dr = new DrawingClass(
                {
                    id: id || uuid().replace(/[-]*/g, '').substring(0, 10),
                    displayType,
                    referenceDrawingId: referenceId,
                    options: { ...options },
                    chart: this.chart,
                    settings: { ...settings },
                    initialState,
                    paneId,
                    seriesId,
                    magnetMode: this.magnetMode,
                    onDestroy: this.onDrawingDestroy,
                    onFocusChange: this.onFocusChange,
                    onDrawingInactive: this.onDrawingInactive,
                    onDrawingActive: this.onDrawingActive,
                    onDrawingComplete: this.onDrawingComplete,
                    onDrawingUpdate: this.onDrawingUpdate
                })
            this.drawings[dr.id] = dr
            this.drawingsSequence.push(dr.id)
            if (!settings.indicator && displayType !== DrawingDisplayType.INDICATOR) {
                this.activateDrawing(dr.id)
            }
            return dr
        }
        return null
    }

    handleDrawingRemove = (event: CustomEvent<string>) => {
        event && event.detail && this.removeDrawing(event.detail)
    }

    getActiveDrawing() {
        return this.drawingsSequence.find((id) => this.drawings[id] && this.drawings[id].mode == 'DRAW')
    }

    getFocusedDrawing() {
        return this.drawingsSequence.find((id) => this.drawings[id] && this.drawings[id]._active)
    }

    onDrawingComplete = (id: string) => {
        this.chart.publishDrawingEvent('end', { id })
        this.updateChartDrawings(true)
    }

    onDrawingUpdate = () => {
        this.updateChartDrawings(undefined)
    }

    onDrawingEvent = (event: DrawingEvents, data: any) => {
        let drawing: DrawingBase<any, any, any> | undefined
        const { id, referenceId, time, price, coordinates } = data

        switch (event) {
            case 'add':
                this.addDrawing(
                    data.type,
                    data.displayType,
                    data.options,
                    data.settings,
                    undefined,
                    data.initialState,
                    this.chart.primarySeriesGroup?.pane.id,
                    this.chart.primarySeriesGroup?.series[0].id,
                    data.id
                )
                break
            case 'sync':
                const dr = this.addDrawing(
                    data.type,
                    data.displayType,
                    data.options,
                    data.settings,
                    undefined,
                    data.initialState,
                    this.chart.primarySeriesGroup?.pane.id,
                    this.chart.primarySeriesGroup?.series[0].id,
                    data.id
                )
                this.updateChartDrawings()
                dr?.toggleFocus(true, false, true)
                break
            case 'add_point':
                const { preview } = data
                drawing = Object.values(this.drawings).find(v => v.referenceDrawingId === id)
                if (!drawing) return
                drawing._onAddPoint(time, price, coordinates, preview, true)
                break
            case 'focused':
                const { focused } = data
                drawing = referenceId ? (this.drawings[referenceId] || Object.values(this.drawings).find(v => v.referenceDrawingId === referenceId)) : Object.values(this.drawings).find(v => v.referenceDrawingId === id)
                if (!drawing) return
                drawing.toggleFocus(focused, false, focused)
                break
            case 'update_handle':
                const { handleId } = data
                drawing = referenceId ? (this.drawings[referenceId] || Object.values(this.drawings).find(v => v.referenceDrawingId === referenceId)) : Object.values(this.drawings).find(v => v.referenceDrawingId === id)
                if (!drawing) return

                drawing._updateHandle(handleId, time, price, coordinates, !referenceId, true)
                break
            case 'move':
                const { handles } = data
                drawing = referenceId ? (this.drawings[referenceId] || Object.values(this.drawings).find(v => v.referenceDrawingId === referenceId)) : Object.values(this.drawings).find(v => v.referenceDrawingId === id)
                if (!drawing) return

                drawing._updateHandles(handles, !referenceId)
                break
            case 'update_settings':
                const { settings } = data
                drawing = referenceId ? (this.drawings[referenceId] || Object.values(this.drawings).find(v => v.referenceDrawingId === referenceId)) : Object.values(this.drawings).find(v => v.referenceDrawingId === id)
                if (!drawing) return

                drawing.updateSettings(settings, !referenceId)
                break
            case 'remove':
                drawing = referenceId ? (this.drawings[referenceId] || Object.values(this.drawings).find(v => v.referenceDrawingId === referenceId)) : Object.values(this.drawings).find(v => v.referenceDrawingId === id)
                if (!drawing) return
                this.removeDrawing(drawing.id)
                break
            case 'unsync':
                drawing = referenceId ? (this.drawings[referenceId] || Object.values(this.drawings).find(v => v.referenceDrawingId === referenceId)) : Object.values(this.drawings).find(v => v.referenceDrawingId === id)
                if (!drawing) return
                this.removeDrawing(drawing.id, false)
                break
            case 'end':
                drawing = referenceId ? this.drawings[referenceId] : Object.values(this.drawings).find(v => v.referenceDrawingId === id)
                if (!drawing) return
                drawing._endDrawing(!referenceId)
                break
            case 'update_state':
                drawing = referenceId ? (this.drawings[referenceId] || Object.values(this.drawings).find(v => v.referenceDrawingId === referenceId)) : Object.values(this.drawings).find(v => v.referenceDrawingId === id)
                if (!drawing) return

                drawing._state = {
                    ...drawing._state,
                    ...data.state
                }
                drawing.updateDrawing()
                break
            case 'abort':
                const activeDrawingId = this.getActiveDrawing()
                if (activeDrawingId && activeDrawingId !== id) {
                    this.abort()
                }

        }
    }

    updateChartDrawings(drawingAdded: boolean | null = null) {
        if (!this.destroyed)
            this.chart.updateChartDrawings(
                this.getAllDrawings(), drawingAdded
            )
    }

    onDrawingDestroy = (drawingId: string, publish = true) => {
        this.drawingsSequence = this.drawingsSequence.filter(i => i !== drawingId)
        publish && this.chart.publishEvent(ChartEvents.DRAWING_DESTROYED, {
            id: drawingId,
            type: this.drawings[drawingId]?.options['drawingType']
        })
        delete this.drawings[drawingId]
    }

    onFocusChange = (id: string, focused: boolean) => {
        const dr = this.drawings[id]
        if (!dr) return
        if (
            dr.options.drawingType === 'contractSwitch' ||
            dr.options.drawingType === 'corporateAction' ||
            dr.options.drawingType === 'companyEvent' ||
            dr.options.drawingType === 'newsArticle' ||
            dr.options.drawingType === 'sessionBreak' ||
            dr.options.drawingType === 'chartOrdersActions' ||
            dr.options.drawingType === 'orderMarkers' ||
            dr.options.drawingType === 'chartAlertsOnChart'
        ) return
        this.chart.publishEvent(ChartEvents.DRAWING_FOCUSED, { id, focused })
        this.chart.publishDrawingEvent('focused', { id, focused, referenceId: dr.referenceDrawingId }, true)
        if (focused) {
            this.addDeleteListener()
        } else {
            this.removeDeleteListener()
        }
    }
    onDrawingInactive = () => {
        this.chart.publishEvent(ChartEvents.DRAWING_INACTIVE)
    }

    onDrawingActive = (drawingTypes: DrawingTypes) => {
        this.chart.publishEvent(ChartEvents.DRAWING_ACTIVE, drawingTypes)
    }

    getAllDrawings = (): ChartDrawingItem[] => {
        const drawings: ChartDrawingItem[] = [];
        this.drawingsSequence.forEach((id) => {
            const dr = this.drawings[id]

            if (dr.getDisplayType() !== DrawingDisplayType.DRAWING) return;
            drawings.push({
                id,
                displayType: dr.getDisplayType(),
                referenceDrawingId: dr.referenceDrawingId,
                type: dr.getDrawingType(),
                options: dr.options,
                state: dr._state,
                seriesId: dr._seriesId,
                paneId: dr._paneId,
                settings: dr.getSettingsValues()
            });
        })

        return drawings;
    }

    applyAllDrawings(drawings: ChartDrawingItem[]) {
        drawings.forEach((dr) => {
            if (this.drawingsSequence.indexOf(dr.id) !== -1) return
            this.addDrawing(dr.type, dr.displayType, dr.options, dr.settings, dr.id, dr.state, dr.paneId, dr.seriesId, dr.referenceDrawingId)
        })
    }

    syncDrawing(id: string) {

        const dr = this.drawings[id]
        const config = this.chart.getDrawingConfig(id)
        dr.linked = true
        if (!dr || !config) return
        this.chart.publishDrawingEvent('sync', {
            id: dr.referenceDrawingId || dr.id,
            type: config.type,
            options: config.options,
            settings: config.settings,
            initialState: dr._state
        }, true)
    }

    unsyncDrawing(id: string) {
        const drawing = this.drawings[id]
        if (!drawing) return
        drawing.linked = false
        const referenceId = drawing.referenceDrawingId
        if (referenceId) {
            drawing.referenceDrawingId = undefined
        }
        this.updateChartDrawings()
        this.chart.publishDrawingEvent('unsync', { id, referenceId }, true)
    }

    activateDrawing(drawingId: string) {
        const dr = this.drawings[drawingId]
        if (!dr) throw 'Invalid Drawing ID: ' + drawingId

        this.drawingsSequence.forEach(id => {
            if (id == drawingId) return
            const currentDrawing = this.drawings[id]
            if (!currentDrawing) return
            currentDrawing.deactivate()
            currentDrawing.toggleFocus(false)
        })

        dr.activate()
    }

    deactivateAllDrawings() {
        this.drawingsSequence.forEach(id => {
            const currentDrawing = this.drawings[id]
            if (!currentDrawing || currentDrawing.referenceDrawingId) return
            currentDrawing.deactivate()
            currentDrawing.toggleFocus(false)
        })

        this.removeDeleteListener()
    }

    addDeleteListener() {
        document.addEventListener('keyup', this.onKeyUp)
    }

    removeDeleteListener() {
        document.removeEventListener('keyup', this.onKeyUp)
    }

    onKeyUp = (event: KeyboardEvent) => {
        const target = event.target as HTMLElement
        if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
            return
        }
        const focusedDrawing = this.getFocusedDrawing()
        if (!focusedDrawing) return
        if (['backspace', 'delete'].indexOf(event.key.toLowerCase()) !== -1) {
            this.removeDrawing(focusedDrawing)
            this.removeDeleteListener()
        }
    }

    updateDrawingSettings(id: string, settings: Record<string, any>) {
        if (!this.drawings[id]) throw 'Invalid drawing Id'
        this.drawings[id].updateSettings(settings)
    }

    getDrawingSettings(id: string): DrawingSettingGroup<any, any>[] {
        if (!this.drawings[id]) throw 'Invalid drawing Id'
        return this.drawings[id].getAvailableSettings()
    }

    getDrawingMiniSettings(id: string): string[] {
        if (!this.drawings[id]) throw 'Invalid drawing Id'
        return this.drawings[id].getMiniSettings() as string[]
    }

    getSettingsValues(id: string): any {
        if (!this.drawings[id]) throw 'Invalid drawing Id'
        return this.drawings[id].getSettingsValues()
    }

    setMagnetMode(enabled: boolean) {
        this.magnetMode = enabled
        if (enabled) {
            window.localStorage.setItem('chart_magnet_mode', 'true')
        } else {
            window.localStorage.removeItem('chart_magnet_mode')
        }

        this.drawingsSequence.forEach((id) => {
            const d = this.drawings[id]
            if (!d) return
            d.magnetMode = this.magnetMode
        })
    }

    getMagnetMode() {
        return this.magnetMode
    }

    abort() {
        const activeDrawing = this.getActiveDrawing()
        if (!activeDrawing) return
        activeDrawing && this.removeDrawing(activeDrawing)
        this.onDrawingInactive()
    }

    removeDrawing(drawingId: string, publish = true, pushToHistory = true) {
        const drawing = this.drawings[drawingId]
        if (!drawing) return
        drawing.toggleFocus(false)
        drawing.destroy(publish, pushToHistory)
        this.updateChartDrawings(false)
        publish && this.chart.publishDrawingEvent('remove', {
            id: drawingId,
            referenceId: drawing.referenceDrawingId
        }, true)
    }

    removeAllDrawings(publish = true) {
        this.drawingsSequence.forEach((id) => {
            const dr = this.drawings[id]
            if (!dr) return
            dr.destroy(publish)
        })

        this.updateChartDrawings()
    }

    removeAllDrawingsForSeries(seriesId: string) {
        this.drawingsSequence.forEach((id) => {
            const dr = this.drawings[id]
            if (!dr || dr._seriesId !== seriesId) return
            dr.destroy(false)
        })

        this.updateChartDrawings()
    }

    getDrawing(id: string) {
        return this.drawings[id];
    }

    hasDrawing(id: string) {
        return !!this.drawings[id];
    }

    destroy() {
        this.destroyed = true
        this.removeAllDrawings(false)
    }
}

export default DrawingManager